import { memo, useMemo, useState } from "react";
import { Responsive, Layouts, Layout } from "react-grid-layout";
import { ReactSVG } from "react-svg";
import { useObserveElementWidth } from "helpers";
import { OverviewWidgetProps } from "./types";

import TotalVehiclesCard from "./components/TotalVehiclesCard";
import RegistrationExpiringCard from "./components/RegistrationExpiringCard";
import InsuranceExpiringCard from "./components/InsuranceExpiringCard";
import CascoExpiringCard from "./components/CascoExpiringCard";
import FireExtinguisherExpiringCard from "./components/FireExtinguisherExpiringCard";
import RevenueExpenseCard from "./components/RevenueExpenseCard";
import OffersCard from "./components/OffersCard";
import InvoicesCard from "./components/InvoicesCard";
import InquiriesCard from "./components/InquiriesCard";
import OverviewCard from "./components/OverviewCard";

import { DASHBOARD_STATE_KEY, OVERVIEW_LAYOUTS_KEY } from "../../storage";

/**
 * Breakpointi gledaju širinu ovog widgeta, ne prozora — mreža je ugniježđena, pa je
 * mjerodavno koliko mjesta ima kartica, a ne koliko ekran.
 */
const BREAKPOINTS = { lg: 900, md: 680, sm: 480, xxs: 0 };
const COLS = { lg: 12, md: 8, sm: 4, xxs: 2 };

/**
 * Kartice pregleda. `minW`/`minH` su donja granica ispod koje sadržaj više ne stane —
 * grafovi i iznosi traže više mjesta od golog broja.
 */
const ALL_OVERVIEW_CARDS: {
  id: string;
  w: number;
  h: number;
  minW: number;
  minH: number;
  component: () => React.ReactNode;
}[] = [
  { id: "overview-total-vehicles", w: 3, h: 5, minW: 2, minH: 4, component: () => <TotalVehiclesCard /> },
  { id: "overview-registration", w: 3, h: 5, minW: 2, minH: 4, component: () => <RegistrationExpiringCard /> },
  { id: "overview-insurance", w: 3, h: 5, minW: 2, minH: 4, component: () => <InsuranceExpiringCard /> },
  { id: "overview-casco", w: 3, h: 5, minW: 2, minH: 4, component: () => <CascoExpiringCard /> },
  { id: "overview-fire-extinguisher", w: 3, h: 5, minW: 2, minH: 4, component: () => <FireExtinguisherExpiringCard /> },
  { id: "overview-revenue-expense", w: 6, h: 5, minW: 3, minH: 5, component: () => <RevenueExpenseCard /> },
  { id: "overview-offers", w: 3, h: 5, minW: 2, minH: 5, component: () => <OffersCard /> },
  { id: "overview-invoices", w: 3, h: 5, minW: 2, minH: 5, component: () => <InvoicesCard /> },
  // { id: "overview-inquiries", w: 3, h: 5, minW: 2, minH: 5, component: () => <InquiriesCard /> },
];

const OverviewWidget: React.FC<OverviewWidgetProps> = () => {
  const savedState = JSON.parse(localStorage.getItem(DASHBOARD_STATE_KEY) || "null");
  const activeCardIds: string[] = savedState?.subWidgets?.["widget-overview"] ?? ALL_OVERVIEW_CARDS.map((c) => c.id);

const activeCards = useMemo(
  () => ALL_OVERVIEW_CARDS.filter((card) => activeCardIds.includes(card.id)),
  [activeCardIds]
);
  const savedLayouts: Layouts | null = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(OVERVIEW_LAYOUTS_KEY) || "null");
    } catch {
      return null;
    }
  }, []);

  const defaultLayouts = useMemo((): Layouts => {
    const lg: Layout[] = [];
    let x = 0;
    let y = 0;
    for (const card of activeCards) {
      if (x + card.w > 12) {
        x = 0;
        y += 5;
      }
      lg.push({
        i: card.id,
        x,
        y,
        w: card.w,
        h: card.h,
        minW: card.minW,
        minH: card.minH,
      });
      x += card.w;
    }
    return { lg };
  }, [activeCards]);

  // Spremljeni raspored se zadržava, ali se kartice manje od svog minimuma podignu —
  // stari raspored je nastao dok su minimumi bili niži.
  const [layouts, setLayouts] = useState<Layouts>(() => {
    if (!savedLayouts) return defaultLayouts;

    const minimums = new Map(ALL_OVERVIEW_CARDS.map((card) => [card.id, card]));

    return Object.fromEntries(
      Object.entries(savedLayouts).map(([breakpoint, items]) => [
        breakpoint,
        items.map((item) => {
          const card = minimums.get(item.i);
          if (!card) return item;

          return {
            ...item,
            w: Math.max(item.w, card.minW),
            h: Math.max(item.h, card.minH),
            minW: card.minW,
            minH: card.minH,
          };
        }),
      ])
    ) as Layouts;
  });

  // Mreža je ugniježđena, pa `WidthProvider` ne dolazi u obzir: on mjeri samo na
  // window.resize, a širina ovog widgeta se mijenja i bez toga (povlačenje i promjena
  // veličine widgeta, sklapanje sidebara, lijeno montiranje). Do prve izmjere bi
  // pretpostavio 1280px i posložio kartice po koordinatama koje ne stanu u karticu.
  const { width, ref } = useObserveElementWidth<HTMLDivElement>();

  const handleLayoutChange = (_: Layout[], allLayouts: Layouts) => {
    setLayouts(allLayouts);
    localStorage.setItem(OVERVIEW_LAYOUTS_KEY, JSON.stringify(allLayouts));
  };

  return (
    <div ref={ref} className="w-full">
      {/* Bez izmjerene širine mreža se ne crta — inače prvi render ode na pretpostavku. */}
      {width > 0 && (
        <Responsive
          className="layout"
          width={width}
          layouts={layouts}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={50}
          isResizable
          isDraggable
          draggableHandle=".overview-drag-handle"
          onLayoutChange={handleLayoutChange}
          compactType="vertical"
          margin={[16, 16]}
        >
          {activeCards.map((card) => (
            <div
              key={card.id}
              className="rounded-sm ring-1 ring-slate-200 bg-white relative group/card overflow-hidden"
            >
              <span className="overview-drag-handle absolute top-2 right-2 cursor-move opacity-0 group-hover/card:opacity-100 transition-opacity z-10 hover:bg-slate-50 rounded p-0.5">
                <ReactSVG src="/media/icons/drag-and-drop.svg" className="w-4 h-4 stroke-slate-400" />
              </span>
              <OverviewCard>{card.component()}</OverviewCard>
            </div>
          ))}
        </Responsive>
      )}
    </div>
  );
};

export default memo(OverviewWidget);
